/**
 * Login page for Identity Platform authentication
 * Supports: Email/Password sign-in with forgot password, Google Sign-In
 *
 * NOTE: Self-registration is disabled. Users are managed via scripts/manage_users.py
 */

import { useState, FormEvent } from 'react';
import { Mail, Lock, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Alert, Button, Field, Input } from '@/components/ui';

type AuthMode = 'signin' | 'forgot-password';

function BrandLogo() {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return <span className="font-display text-2xl font-semibold tracking-tight">Simon Loos</span>;
  }
  return <img src="/brand/simon-loos-logo.svg" alt="Simon Loos" className="h-11 w-auto" onError={() => setFailed(true)} />;
}

export default function Login() {
  const {
    signInWithGoogle,
    signInWithEmail,
    resetPassword,
    error,
    successMessage,
    clearMessages,
  } = useAuth();

  const [mode, setMode] = useState<AuthMode>('signin');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleModeChange = (newMode: AuthMode) => {
    setMode(newMode);
    clearMessages();
    setPassword('');
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      await signInWithGoogle();
    } catch {
      // Error is handled in context
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await signInWithEmail(email, password);
    } catch {
      // Error is handled in context
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await resetPassword(email);
    } catch {
      // Error is handled in context
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-pop">
          {/* Brand accent */}
          <div className="h-1.5 bg-sl-yellow" aria-hidden />

          <div className="px-7 pb-7 pt-8">
            {/* Logo and title */}
            <div className="mb-7 flex flex-col items-center text-center">
              <BrandLogo />
              <p className="mt-3 text-xs font-semibold uppercase tracking-[0.18em] text-sl-yellow-dark">BI Agents</p>
              <h1 className="mt-4 text-xl font-semibold">
                {mode === 'signin' ? 'Inloggen' : 'Wachtwoord herstellen'}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {mode === 'signin' ? 'Log in om verder te gaan' : 'We sturen je een link om een nieuw wachtwoord in te stellen'}
              </p>
            </div>

            {mode === 'forgot-password' && (
              <button
                onClick={() => handleModeChange('signin')}
                className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" />
                Terug naar inloggen
              </button>
            )}

            {error && (
              <Alert tone="danger" className="mb-5">
                {error}
              </Alert>
            )}

            {successMessage && (
              <Alert tone="success" className="mb-5">
                {successMessage}
              </Alert>
            )}

            {/* Sign-in form */}
            {mode === 'signin' && (
              <form onSubmit={handleEmailSignIn} className="space-y-4">
                <Field label="E-mailadres" htmlFor="email">
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="naam@simonloos.nl"
                      required
                      className="h-11 pl-9"
                    />
                  </div>
                </Field>

                <Field label="Wachtwoord" htmlFor="password">
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Je wachtwoord"
                      required
                      className="h-11 pl-9 pr-11"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-muted-foreground transition-colors hover:text-foreground"
                      aria-label={showPassword ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </Field>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => handleModeChange('forgot-password')}
                    className="text-sm font-medium text-foreground underline-offset-2 hover:underline"
                  >
                    Wachtwoord vergeten?
                  </button>
                </div>

                <Button type="submit" size="lg" className="w-full" loading={isLoading} disabled={!email || !password}>
                  {isLoading ? 'Bezig met inloggen…' : 'Inloggen'}
                </Button>
              </form>
            )}

            {/* Forgot-password form */}
            {mode === 'forgot-password' && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Vul je e-mailadres in, dan sturen we je een link om je wachtwoord opnieuw in te stellen.
                </p>

                <Field label="E-mailadres" htmlFor="resetEmail">
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="resetEmail"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="naam@simonloos.nl"
                      required
                      className="h-11 pl-9"
                    />
                  </div>
                </Field>

                <Button type="submit" size="lg" className="w-full" loading={isLoading} disabled={!email}>
                  {isLoading ? 'Versturen…' : 'Herstellink versturen'}
                </Button>
              </form>
            )}

            {/* Divider + Google sign-in */}
            {mode === 'signin' && (
              <>
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-card px-2 text-muted-foreground">of</span>
                  </div>
                </div>

                <Button variant="outline" size="lg" className="w-full gap-3" onClick={handleGoogleSignIn} disabled={isLoading}>
                  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden>
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {isLoading ? 'Bezig met inloggen…' : 'Inloggen met Google'}
                </Button>
              </>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">Alleen toegankelijk voor geautoriseerde gebruikers</p>
      </div>
    </div>
  );
}
